/// <reference path="index.ts" />
import { useMemo } from "react";
import { Pageable } from "../../../../services/pageable";
import { getFields, validationFields } from "../../../common/models/fields";
import { StatusEnum } from "../../../users/models/internal";
import { DeclineCode } from "../internal";

export namespace VerificationDTO {
  export namespace Responses {
    export namespace GetSingleIndividualAml {
      export interface T {
        createdDate: Date;
        declineCodes: Array<DeclineCode> | null;
        filters: Array<string> | null;
        fraudCheckId: string;
        ongoingChecks: boolean;
        status: StatusEnum;
        user: {
          address: string | null;
          dateOfBirth: Date | null;
          emailAddress: string | null;
          firstName: string;
          identityDocument: string | null;
          language: string | null;
          lastName: string;
          middleName: string | null;
        };
      }

      export const defaults: Partial<T> = {
        fraudCheckId: "",
      };
    }

    export namespace GetAllIndividualAml {
      export interface T {
        content: GetSingleIndividualAml.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }

    export namespace GetSingleBusinessAml {
      export interface T {
        companyName: string;
        createdDate: Date;
        declineCodes: Array<DeclineCode> | null;
        filters: Array<string> | null;
        fraudCheckId: string;
        incorporationDate: Date | null;
        status: StatusEnum;
      }
    }

    export namespace GetAllBusinessAml {
      export interface T {
        content: GetSingleBusinessAml.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }

    export namespace UploadIndividualAml {
      export interface T {}
      export const fields = getFields({});
    }

    export namespace UploadBusinessAml {
      export type T = UploadIndividualAml.T;

      export const fields = UploadIndividualAml.fields;
    }
  }
  export namespace Requests {
    export namespace UploadIndividualAml {
      export interface T {
        fraudCheckId: string;
        firstName: string;
        lastName: string;
        middleName: string;
        dateOfBirth: Date;
        ongoingChecks: boolean;
      }

      export const useFields = () => {
        const fraudCheckIdSchema = validationFields.fraudCheckId.useSchema();
        const firstNameSchema = validationFields.firstName.useSchema();
        const lastNameSchema = validationFields.lastName.useSchema();
        const middleNameSchema = validationFields.middleName.useSchema();
        const dateOfBirthSchema = validationFields.dateOfBirth.useSchema();
        const ongoingBackgroundChecksSchema = validationFields.ongoingBackgroundChecks.useSchema();

        const fraudCheckId = {
          name: validationFields.fraudCheckId.useName(),
          schema: fraudCheckIdSchema,
        };
        const firstName = {
          name: validationFields.firstName.useName(),
          schema: firstNameSchema,
        };
        const lastName = {
          name: validationFields.lastName.useName(),
          schema: lastNameSchema,
        };
        const middleName = {
          name: validationFields.middleName.useName(),
          schema: middleNameSchema,
        };
        const dateOfBirth = {
          name: validationFields.dateOfBirth.useName(),
          schema: dateOfBirthSchema,
        };
        const ongoingChecks = {
          name: validationFields.ongoingBackgroundChecks.useName(),
          schema: ongoingBackgroundChecksSchema,
        };
        return useMemo(
          () =>
            getFields({
              fraudCheckId,
              firstName,
              lastName,
              middleName,
              dateOfBirth,
              ongoingChecks,
            }),
          [
            fraudCheckId.name,
            fraudCheckId.schema,
            firstName.name,
            firstName.schema,
            lastName.name,
            lastName.schema,
            middleName.name,
            middleName.schema,
            dateOfBirth.name,
            dateOfBirth.schema,
            ongoingChecks.name,
            ongoingChecks.schema,
          ]
        );
      };

      export const defaults: Partial<T> = {};
    }

    export namespace UploadBusinessAml {
      export interface T {
        companyName: string;
        fraudCheckId: string;
        companyIncorporationDate: Date;
        ongoingCheck: boolean;
      }
      export const useFields = () => {
        const companyNameSchema = validationFields.companyName.useSchema();
        const fraudCheckIdSchema = validationFields.fraudCheckId.useSchema();
        const companyIncorporationDateSchema = validationFields.companyIncorporationDate.useSchema();
        const ongoingCheckSchema = validationFields.ongoingCheck.useSchema();

        const companyName = {
          name: validationFields.companyName.useName(),
          schema: companyNameSchema,
        };
        const fraudCheckId = {
          name: validationFields.fraudCheckId.useName(),
          schema: fraudCheckIdSchema,
        };
        const companyIncorporationDate = {
          name: validationFields.companyIncorporationDate.useName(),
          schema: companyIncorporationDateSchema,
        };
        const ongoingCheck = {
          name: validationFields.ongoingCheck.useName(),
          schema: ongoingCheckSchema,
        };

        return useMemo(
          () =>
            getFields({
              companyName,
              fraudCheckId,
              companyIncorporationDate,
              ongoingCheck,
            }),
          [
            companyName.name,
            companyName.schema,
            fraudCheckId.name,
            fraudCheckId.schema,
            companyIncorporationDate.name,
            companyIncorporationDate.schema,
            ongoingCheck.name,
            ongoingCheck.schema,
          ]
        );
      };
      export const defaults: Partial<T> = {
        ongoingCheck: false,
      };
    }
  }
}
